<script setup lang="ts">
import stores from './storage/stores'
import { computed, defineAsyncComponent, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
    applyTheme,
    isOtherThemeHappy,
    isThemeDefault,
    isThemeHappy,
    isThemeNoct
} from './event/theme/theme'
import { loadData } from './utils/loadData'
import { dataLoaded } from './event/dataLoaded'
import { watchersApp } from './utils/watchers/app/watchersApp'
import { formatStoreName } from '@/utils/store/formatStoreName'
import { showSectionManagement } from './event/main/app/showSectionManagement'
import { clientConfig } from './config/clientConfig'
import { useHead } from '@vueuse/head'
import { url } from './event/url/url'
import DefaultLayout from './layout/DefaultLayout.vue'
import HappyLayout from './layout/HappyLayout.vue'
import { detectClient } from './utils/config/detectClient'
import { getStatusStoreHappy } from './happyTheme/utils/store/getStatusStoreHappy'
import { checkAndRefreshTokenV2 } from './utils/jwt/checkAndRefreshTokenV2'
import ButtonThemeLocal from './components/main/ButtonThemeLocal.vue'

const Management = defineAsyncComponent(
    () => import('@/components/management/Management.vue')
)

const route = useRoute()

const clientId = detectClient()
const client = Object.values(clientConfig).find((client) => client.hostname === clientId)

const routeTitleMapName: Record<string, string> = {
    'catalog-product': 'Catalogue de produits',
    product: 'Produit',
    basket: 'Panier',
    login: 'Connexion',
    register: 'Inscription',
    'confirm-register': "Confirmation d'inscription",
    forget: 'Mot de passe oublié',
    'basket-order': 'Panier - Commande',
    'success-payment': 'Paiement réussi',
    'cancel-payment': 'Erreur de paiement',
    dashboard: 'Tableau de bord',
    'dashboard-edit': 'Tableau de bord - Modification des informations personnelles',
    'dashboard-password': 'Tableau de bord - Modification du mot de passe',
    'dashboard-address': 'Tableau de bord - Gestion des adresses',
    'dashboard-order': 'Tableau de bord - Historique des commandes',
    'cancel-delete': 'Annuler la suppression du compte',
    'privacy-policy': 'Politique de confidentialité',
    'condition-use': "Conditions d'utilisation"
}

const pageTitle = computed(() => {
    const routeName = route.name as string
    const title = routeTitleMapName[routeName] ? `| ${routeTitleMapName[routeName]}` : ''
    return `${client?.title} ${title}`
})

const canonicalUrl = computed(() => {
    const path = route.path
    const returnCanonicalUrl = `${url.protocol}${url.host}${path}`
    return returnCanonicalUrl
})

useHead({
    title: pageTitle,
    meta: [{ name: 'description', content: client?.description }],
    link: [
        { rel: 'icon', href: `${client?.urlBack}${client?.favicon}` },
        { rel: 'apple-touch-icon', href: `${client?.urlBack}${client?.favicon}` },
        { rel: 'manifest', href: `${client?.urlBack}${client?.favicon}` },
        {
            rel: 'canonical',
            href: canonicalUrl
        }
    ]
})

watchersApp(route)

onMounted(async () => {
    const savedTheme = localStorage.getItem('theme')
    if (savedTheme) {
        applyTheme(savedTheme)
    }

    await loadData()

    let initialLoad = true
    if (isThemeHappy.value || isOtherThemeHappy.value) {
        checkAndRefreshTokenV2()
    }

    document.addEventListener('visibilitychange', async () => {
        if (document.visibilityState === 'visible' && !initialLoad) {
            await loadData()
            if (isThemeHappy.value || isOtherThemeHappy.value) {
                getStatusStoreHappy()

                checkAndRefreshTokenV2()
            }
        }

        initialLoad = false
    })

    watch([() => route.name, () => dataLoaded.value], ([routeName, dataLoadedValue]) => {
        if (dataLoadedValue === 2) {
            const store = stores.state.storeData.name
            if (store) {
                const storeName = formatStoreName(store)
                const cartKey = `${storeName}_cart`

                if (routeName === 'success-payment') {
                    localStorage.removeItem(cartKey)
                }
            }
        }
    })
})
</script>

<template>
    <div class="content-site" v-if="showSectionManagement === false">
        <ButtonThemeLocal v-if="isThemeNoct" />
        <DefaultLayout v-if="isThemeDefault" />
        <HappyLayout v-if="isThemeHappy || isOtherThemeHappy" />
    </div>

    <Management v-else />
</template>

<style scoped>
.content-site {
    @apply flex flex-col items-center justify-between w-full h-full font-satoshi happy-light:font-montserrat happy-dark:font-montserrat;
}
</style>
